import { useTheme } from "@mui/material/styles";
import { hasAnyAccessToResource } from "api/general";
import { useMember } from "api/members";
import { useTiers } from "api/tiers";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { alphabetizeByProperty } from "utilities/arrays";
import { links } from "utilities/links";
import { CircleGraph } from "./CircleGraph";
import { MembersByTiersTooltip } from "./MembersByTiersTooltip";

const OPACITY_ARRAY = ["FF", "D1", "A6", "78", "4D"];

interface TierItem {
  name: string;
  customers: number;
  levelId: string;
}

export function MembersByTiers() {
  const theme: OLTheme = useTheme();
  const { message } = useMessage();
  const navigate = useNavigate();

  const { useMemberAnalytics } = useMember();
  const { useTierStatistics } = useTiers();

  const { data: membersTotal } = useMemberAnalytics();
  const { data: tierStatistics } = useTierStatistics();

  const createGraphItems = useMemo(
    () => (items: TierItem[]) => {
      return items.map((item, index) => ({
        id: item.levelId,
        label: item.name,
        value: item.customers,
        color: `${theme.primary500}${
          index < OPACITY_ARRAY.length
            ? OPACITY_ARRAY[index]
            : OPACITY_ARRAY.slice(-1)
        }`,
      }));
    },
    [theme.primary500],
  );

  const graphItems = useMemo(() => {
    if (!tierStatistics?.levels) {
      return [{ color: theme.gray50, id: "", label: "", value: 100 }];
    }

    const sortedLevels = tierStatistics.levels.sort(
      (a: TierItem, b: TierItem) => b.customers - a.customers,
    );
    return createGraphItems(sortedLevels);
  }, [tierStatistics, theme.gray50, createGraphItems]);

  const contextMenuActions = useMemo(() => {
    const hasAnyTierPermission = hasAnyAccessToResource("LEVEL");
    const actions = [
      {
        action: () => navigate(links.membersList),
        label: message({ id: "seeTheListOfMembers" }),
      },
    ];

    if (hasAnyTierPermission) {
      actions.push({
        action: () => navigate(links.tiersList),
        label: message({ id: "seeTheListOfTiers" }),
      });
    }

    return alphabetizeByProperty(actions, "label");
  }, [message, navigate]);

  return (
    <CircleGraph
      centralText={{
        number: membersTotal?.total,
        text: message({ id: "allMembers" }),
        tooltip: message({ id: "totalNumberOfMembers" }),
      }}
      contextActions={contextMenuActions}
      items={graphItems}
      tooltipElement={MembersByTiersTooltip}
      isLoading={!tierStatistics}
    />
  );
}
